/**
 * 출장 일지 등록/수정 다이얼로그.
 *
 * 저장 대상은 일정/메모와 다른 엔티티(site_visit_logs)라 폼을 하나로 합칠 수는
 * 없지만, 사용자에게는 **종류만 다른 같은 폼** 으로 보여야 한다. 그래서 최상단
 * 종류 선택기·초안 자동 저장·모바일 전체화면·sticky 액션바를 일정 폼과 똑같이
 * 맞췄다 (공용 조각은 formParts / RecordKindSelector).
 *
 * 필드는 출장 일지 고유의 것만 남긴다: 방문일 · 방문자 · 목적 · 작업 내용 ·
 * 후속 조치. 방문자는 가입자 디렉토리 Autocomplete(multiple·freeSolo)로 여러 명을
 * 콤마로 보관하며 칩에 프로필 이미지를 붙인다.
 *
 * 사진은 작업 내용/후속 조치 에디터 본문에 인라인으로 첨부한다(툴바·붙여넣기·
 * 드래그). 수정 모드에서는 과거 attachments_csv 를 그대로 보존해 기존 데이터
 * 유실을 막는다.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  DescriptionOutlined as VisitLogIcon,
} from '@mui/icons-material';
import RichTextEditor from '../editor/RichTextEditor';
import { useEditorImageUpload } from '../editor/useEditorImageUpload';
import RecordKindSelector, {
  RecordCarry,
  RecordKind,
  RecordKindHint,
} from './RecordKindSelector';
import {
  DraftBanner,
  DraftSavedHint,
  SectionLabel,
  stickyActionsSx,
  stickyTitleSx,
} from './formParts';
import useFormDraft from '../../hooks/useFormDraft';
import {
  SiteVisitLog,
  CreateSiteVisitLogRequest,
  UpdateSiteVisitLogRequest,
} from '../../models/SiteVisitLog';
import { Project } from '../../models/Project';
import { SiteVisitLogService } from '../../services/SiteVisitLogService';
import useUserDirectory from '../../hooks/useUserDirectory';
import { withMediaToken } from '../../utils/media';
import { colorFromSeed } from '../../utils/colorFromSeed';

interface FormState {
  /** selectableSites 모드에서만 사용. */
  siteId: string;
  visited_at: string;
  visitor_name: string;
  purpose: string;
  work_summary: string;
  next_action: string;
}

const EMPTY: FormState = {
  siteId: '',
  visited_at: '',
  visitor_name: '',
  purpose: '',
  work_summary: '',
  next_action: '',
};

const toIso = (localDt: string): string => {
  if (!localDt) return '';
  const d = new Date(localDt);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString();
};

const toLocalInput = (iso: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromLog = (log: SiteVisitLog): FormState => ({
  siteId: String(log.site_project_id),
  visited_at: toLocalInput(log.visited_at),
  visitor_name: log.visitor_name ?? '',
  purpose: log.purpose ?? '',
  work_summary: log.work_summary ?? '',
  next_action: log.next_action ?? '',
});

const toRequest = (form: FormState, attachmentsCsv: string): CreateSiteVisitLogRequest => ({
  visited_at: toIso(form.visited_at),
  visitor_name: form.visitor_name.trim(),
  purpose: form.purpose.trim(),
  work_summary: form.work_summary || undefined,
  next_action: form.next_action || undefined,
  attachments_csv: attachmentsCsv,
});

/** "a, b" → ["a","b"]. 빈/공백 토큰 제거. */
const splitNames = (csv: string): string[] =>
  csv
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

/** 사용자가 실제로 뭔가 쓴 폼인가 — 빈 폼은 초안으로 저장하지 않는다. */
const isBlankForm = (f: FormState): boolean =>
  !f.purpose.trim() && !f.work_summary.trim() && !f.next_action.trim();

/** 이름 → 이니셜 (한글은 첫 글자, 영문은 최대 2글자). 아바타 fallback 용. */
function initialsOf(name: string): string {
  const src = name.trim();
  if (!src) return '?';
  if (/[가-힣]/.test(src)) return src.charAt(0);
  const parts = src.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return src.charAt(0).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export interface VisitLogFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** 단일 현장 컨텍스트. selectableSites 와 둘 중 하나. 수정 모드는 editing 우선. */
  projectId?: number;
  /** 단일 현장 컨텍스트에서 헤더에 보여줄 현장 이름. */
  siteName?: string;
  /** 현장 선택 모드(현장 목록 페이지 등 현장 무관 진입점). */
  selectableSites?: Project[];
  /** 수정 대상. 없으면 등록 모드. */
  editing?: SiteVisitLog | null;
  /**
   * 등록 모드에서 visited_at 의 기본값을 강제할 때 (예: 캘린더에서 날짜 선택 후 등록).
   * "YYYY-MM-DD" 또는 ISO. editing 이 있으면 무시됨.
   */
  defaultVisitedAt?: string;
  /** 저장 성공 시 호출. 부모는 보통 list refresh + snack. */
  onSaved?: (log: SiteVisitLog | null) => void;
  /** 최상단 종류 선택기에 노출할 선택지. */
  availableKinds?: RecordKind[];
  /** 이 폼이 처리할 수 없는 종류가 선택됐을 때 부모에게 넘긴다. */
  onKindChange?: (kind: RecordKind, carry: RecordCarry) => void;
  /** 다른 종류에서 옮겨올 때 이어받는 공통 입력값. */
  initialCarry?: RecordCarry | null;
}

const VisitLogFormDialog: React.FC<VisitLogFormDialogProps> = ({
  open,
  onClose,
  projectId,
  siteName,
  selectableSites,
  editing,
  defaultVisitedAt,
  onSaved,
  availableKinds,
  onKindChange,
  initialCarry,
}) => {
  const theme = useTheme();
  // 다크모드에서 datetime-local 의 네이티브 아이콘이 검정으로 그려져 안 보이는 문제.
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [form, setForm] = useState<FormState>(EMPTY);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { users, names: userNames } = useUserDirectory();
  const mentionUsers = useMemo(() => users.map((u) => ({ id: u.id, name: u.name })), [users]);

  /** 방문일이 폼이 대신 채운 값인가 — 메모로 옮길 때 버릴지 판단한다. */
  const dateAutoSeededRef = useRef(false);

  const isSelectMode = selectableSites != null && selectableSites.length > 0;

  const initialSiteId = useMemo(() => {
    if (projectId != null) return String(projectId);
    if (selectableSites && selectableSites.length === 1) return String(selectableSites[0].id);
    return '';
  }, [projectId, selectableSites]);

  // 인라인 이미지 업로드 대상 현장 — 선택 모드에서 미선택이면 비활성.
  const imageSiteId = projectId != null ? projectId : Number(form.siteId);
  const canUploadImage = Number.isFinite(imageSiteId) && imageSiteId > 0;
  const uploadImage = useEditorImageUpload(canUploadImage ? imageSiteId : 0);

  // 방문자는 백엔드 컬럼 1개에 CSV 로 들어간다 — 표시용으로만 배열로 다룬다.
  const visitorNames = useMemo(() => splitNames(form.visitor_name), [form.visitor_name]);

  /** 이름(소문자) → userId. 방문자 칩에 프로필 이미지를 붙이기 위한 조회용. */
  const userIdByName = useMemo(
    () => new Map(users.map((u) => [u.name.trim().toLowerCase(), u.id] as const)),
    [users],
  );

  /**
   * 초안 키 — 수정은 대상 id 로, 신규는 현장으로 나눈다. 한 키를 공유하면
   * A 현장에 쓰던 초안이 B 현장 등록 폼에 나타난다.
   */
  const draftKey = editing
    ? `visitlog:edit:${editing.id}`
    : `visitlog:new:${projectId ?? 'pick'}`;
  const { recovered, savedAt, dismissRecovered, clear: clearDraftState } = useFormDraft(form, {
    key: draftKey,
    enabled: open,
    isEmpty: isBlankForm,
  });

  // 열릴 때마다 editing(수정) 또는 carry/preset(신규)으로 초기화.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm(fromLog(editing));
      dateAutoSeededRef.current = false; // 저장된 방문일은 실제 데이터다
    } else {
      const presetSeed = defaultVisitedAt
        ? toLocalInput(
            defaultVisitedAt.includes('T') ? defaultVisitedAt : `${defaultVisitedAt}T09:00:00`,
          )
        : '';
      const carried = initialCarry?.datetimeLocal ?? '';
      const seed = carried || presetSeed || toLocalInput(new Date().toISOString());
      // 이어받은 값도 preset 도 없으면 "지금" 은 폼이 대신 넣은 값이다.
      dateAutoSeededRef.current = !carried && !presetSeed;
      setForm({
        ...EMPTY,
        siteId: initialCarry?.siteId || initialSiteId,
        visited_at: seed,
        // 필드 이름은 달라도 담는 뜻은 같다 — 제목→목적, 내용→작업 내용, 담당자→방문자.
        purpose: initialCarry?.title ?? '',
        work_summary: initialCarry?.body ?? '',
        visitor_name: (initialCarry?.people ?? []).join(', '),
      });
    }
    setFormError(null);
    setSaving(false);
  }, [open, editing, defaultVisitedAt, initialSiteId, initialCarry]);

  /** 초안 이어쓰기 — 저장된 값을 폼에 적용하고 배너를 닫는다. */
  const applyRecovered = useCallback(() => {
    if (!recovered) return;
    setForm(recovered.value);
    dismissRecovered();
  }, [recovered, dismissRecovered]);

  /** 종류를 옮길 때 함께 넘길 공통 입력값. */
  const handleKindChange = (next: RecordKind) => {
    onKindChange?.(next, {
      siteId: form.siteId,
      title: form.purpose,
      body: form.work_summary,
      people: visitorNames,
      datetimeLocal: form.visited_at,
      datetimeAuto: dateAutoSeededRef.current,
    });
  };

  /**
   * 노출할 선택지. 부모가 전환을 받아줄 수 없거나 수정 모드면 선택기를 숨긴다 —
   * 저장된 출장 일지를 일정으로 바꿀 수는 없다(엔티티가 다르다).
   */
  const kinds = useMemo<RecordKind[]>(
    () => (onKindChange && !editing ? availableKinds ?? ['visitlog'] : ['visitlog']),
    [availableKinds, onKindChange, editing],
  );

  const validate = (): string | null => {
    if (isSelectMode && (!form.siteId || Number(form.siteId) <= 0)) return '현장을 선택하세요.';
    if (!form.visited_at) return '방문일을 입력하세요.';
    if (!form.visitor_name.trim()) return '방문자 이름을 입력하세요.';
    if (!form.purpose.trim()) return '방문 목적을 입력하세요.';
    const iso = toIso(form.visited_at);
    if (!iso) return '방문일 형식이 올바르지 않습니다.';
    if (new Date(iso).getTime() > Date.now() + 24 * 60 * 60 * 1000) {
      // 아직 다녀오지 않은 방문은 일정으로 남기는 게 맞다.
      return '방문일은 미래일 수 없습니다. 예정된 방문이면 일정으로 등록하세요.';
    }
    return null;
  };

  const handleSave = async () => {
    const v = validate();
    if (v) {
      setFormError(v);
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      // 사진은 에디터 본문에 인라인 저장되므로 attachments_csv 는 수정 시 기존 값만 보존.
      const attachmentsCsv = editing?.attachments_csv ?? '';
      let saved: SiteVisitLog | null;
      if (editing) {
        const req: UpdateSiteVisitLogRequest = toRequest(form, attachmentsCsv);
        saved = (await SiteVisitLogService.update(editing.id, req)) ?? null;
      } else {
        const targetSiteId = projectId != null ? projectId : Number(form.siteId);
        saved =
          (await SiteVisitLogService.create(targetSiteId, toRequest(form, attachmentsCsv))) ?? null;
      }
      clearDraftState();
      onSaved?.(saved);
      onClose();
    } catch (e) {
      // 저장이 실패했으면 초안은 절대 지우지 않는다 — 지금이 초안이 가장 필요한 순간이다.
      setFormError(e instanceof Error ? e.message : '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  const selectedSite = useMemo(
    () => selectableSites?.find((s) => String(s.id) === form.siteId) ?? null,
    [selectableSites, form.siteId],
  );

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={stickyTitleSx}>
        <VisitLogIcon color="action" />
        <Box component="span" sx={{ flex: 1, fontSize: '1.05rem', fontWeight: 700 }}>
          {editing ? '출장 일지 수정' : '출장 일지 등록'}
        </Box>
        <IconButton onClick={onClose} disabled={saving} aria-label="닫기" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
        <Stack spacing={2.5}>
          {recovered && (
            <DraftBanner
              savedAt={recovered.savedAt}
              onResume={applyRecovered}
              onDiscard={clearDraftState}
            />
          )}

          {/* 1. 종류 — 일정 폼과 같은 자리, 같은 모양. */}
          {kinds.length > 1 && (
            <Box>
              <SectionLabel>종류</SectionLabel>
              <RecordKindSelector
                value="visitlog"
                kinds={kinds}
                onChange={handleKindChange}
                disabled={saving}
              />
              <RecordKindHint kind="visitlog" />
            </Box>
          )}

          {/* 2. 현장 */}
          {isSelectMode ? (
            <Box>
              <SectionLabel required>현장</SectionLabel>
              <Autocomplete
                options={selectableSites!}
                value={selectedSite}
                onChange={(_, v) => setForm((f) => ({ ...f, siteId: v ? String(v.id) : '' }))}
                getOptionLabel={(s) => s.name_ko || s.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField {...params} placeholder="현장 이름으로 검색" />
                )}
              />
            </Box>
          ) : (
            siteName && (
              <Box>
                <SectionLabel>현장</SectionLabel>
                <Chip label={siteName} color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  현재 현장으로 등록됩니다
                </Typography>
              </Box>
            )
          )}

          {/* 3. 방문 목적 — 일정의 '제목' 자리. */}
          <Box>
            <SectionLabel required>방문 목적</SectionLabel>
            <TextField
              value={form.purpose}
              onChange={(e) => setForm((f) => ({ ...f, purpose: e.target.value }))}
              fullWidth
              placeholder="예: 정기 점검, 긴급 출동, 설치"
              inputProps={{ 'aria-label': '방문 목적' }}
            />
          </Box>

          {/* 4. 방문자 */}
          <Box>
            <SectionLabel required>방문자</SectionLabel>
            <Autocomplete
              multiple
              freeSolo
              options={userNames}
              value={visitorNames}
              onChange={(_, v) =>
                setForm((f) => ({
                  ...f,
                  visitor_name: (v as string[])
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .join(', '),
                }))
              }
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const userId = userIdByName.get(option.toLowerCase());
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      {...tagProps}
                      size="small"
                      label={option}
                      avatar={
                        <Avatar
                          src={
                            userId
                              ? withMediaToken(`/v0.1/auth/users/${userId}/profile-image`)
                              : undefined
                          }
                          sx={{ bgcolor: colorFromSeed(option), color: '#fff', fontSize: '0.7rem' }}
                          imgProps={{
                            onError: (e) => {
                              (e.currentTarget as HTMLImageElement).style.display = 'none';
                            },
                          }}
                        >
                          {initialsOf(option)}
                        </Avatar>
                      }
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={visitorNames.length === 0 ? '가입자 선택 또는 직접 입력' : ''}
                  helperText="여러 명 지정 가능. 목록에 없으면 입력 후 Enter."
                />
              )}
            />
          </Box>

          {/* 5. 방문일 */}
          <Box>
            <SectionLabel required>방문일</SectionLabel>
            <TextField
              type="datetime-local"
              value={form.visited_at}
              onChange={(e) => {
                // 직접 고친 날짜는 실제 의도다 — 종류를 옮길 때 유지해야 한다.
                dateAutoSeededRef.current = false;
                setForm((f) => ({ ...f, visited_at: e.target.value }));
              }}
              InputLabelProps={{ shrink: true }}
              // 아이콘뿐 아니라 입력창 빈 공간을 눌러도 네이티브 피커가 뜨게 한다.
              inputProps={{
                'aria-label': '방문일',
                onClick: (e: React.MouseEvent<HTMLInputElement>) =>
                  (e.currentTarget as HTMLInputElement).showPicker?.(),
              }}
              fullWidth
              sx={{ '& input': { colorScheme: isDark ? 'dark' : 'light' } }}
            />
          </Box>

          {/* 6. 작업 내용 */}
          <Box>
            <SectionLabel>작업 내용</SectionLabel>
            <RichTextEditor
              value={form.work_summary}
              onChange={(md) => setForm((f) => ({ ...f, work_summary: md }))}
              placeholder="수행한 작업을 작성하세요. @ 로 동료를 멘션하면 알림이 갑니다. 사진은 툴바·붙여넣기·드래그로 첨부됩니다."
              minHeight={isMobile ? 160 : 200}
              onUploadImage={canUploadImage ? uploadImage : undefined}
              mentionUsers={mentionUsers}
            />
          </Box>

          {/* 7. 후속 조치 */}
          <Box>
            <SectionLabel>후속 조치 / 다음 일정</SectionLabel>
            <RichTextEditor
              value={form.next_action}
              onChange={(md) => setForm((f) => ({ ...f, next_action: md }))}
              placeholder="이어서 처리할 일이나 다음 방문 계획을 작성하세요. @ 로 멘션 가능."
              minHeight={isMobile ? 120 : 140}
              onUploadImage={canUploadImage ? uploadImage : undefined}
              mentionUsers={mentionUsers}
            />
          </Box>

          {formError && <Alert severity="error">{formError}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions sx={stickyActionsSx}>
        <DraftSavedHint savedAt={savedAt} />
        <Button onClick={onClose} disabled={saving}>
          취소
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ minWidth: 96 }}>
          {saving ? '저장 중...' : editing ? '저장' : '등록'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VisitLogFormDialog;
